import { GroupInterface, LocationInterface, UserInterface, UserVarKey, UserVarsMap } from "@swivl-mono/types";
import { Input } from "@swivl-mono/ui";
import { useEffect, useMemo, useState } from "react";

import { DetailsPanel } from "../../../../Components/Conversations/DetailsPanel/DetailsPanel";
import { DataCardLocation } from "../../../../Components/DataCards/DataCard.Location";
import Pill, { PillSize, PillStyle, PillType } from "../../../../Components/Pill/Pill";
import { useUrlState } from "../../../../Hooks/useUrlState";
import LocationsModel from "../../../../Models/Locations/Locations.Model";
import TenantsModel from "../../../../Models/Tenants/Tenants.Model";
import { UnitsModel } from "../../../../Models/Units/Units.Model";
import { FilterDropdown, FilterOption } from "../Components/FilterDropdown";
import { UserDetailsLocationDetailsView } from "./UserDetails.LocationDetailsView";
import { UserDetailsUnitDetailsView } from "./UserDetails.UnitDetailsView";

interface UserDetailsLocationLookupViewProps {
  user: UserInterface;
  userVars: UserVarsMap | undefined;
}

export function UserDetailsLocationLookupView({ user, userVars }: UserDetailsLocationLookupViewProps) {
  const { locationsWithVariables, groups } = LocationsModel.useState((s) => s);
  const locationId = user?.locationId ?
    user.locationId
    : userVars && userVars[UserVarKey.Location]
      ? parseInt(userVars[UserVarKey.Location])
      : undefined;
  const unitsByLocation = UnitsModel.useState((s) => s.unitsByLocation);

  const units = useMemo(() => (locationId ? unitsByLocation?.[locationId] : []), [locationId, unitsByLocation]);
  const { setMultipleSearchParams } = useUrlState();
  const [openPanel, setOpenPanel] = useState(false);
  const [filteredLocationsByGroup, setFilteredLocationsByGroup] = useState(groups || []);
  const [activeFilters, setActiveFilters] = useState<Record<string, Set<string>>>({});

  const sizesFilterOptions = useMemo(() => {
    if (!locationId || !units) return [];
    const sizes = units?.map((unit) => unit.dimensions) || [];
    const uniqueSizes = Array.from(new Set(sizes));
    return uniqueSizes
      .filter(Boolean)
      .map((size) => ({
        label: size?.toString() ?? "--",
        value: size?.toString() ?? "--",
        disabled: false,
        checked: false,
      }))
      .sort((a, b) => {
        const [aWidth, aHeight] = a.value.split("x").map((dimension) => Number(dimension.trim()));
        const [bWidth, bHeight] = b.value.split("x").map((dimension) => Number(dimension.trim()));
        return aWidth - bWidth || aHeight - bHeight;
      });
  }, [units, locationId]);

  const amenitiesFilterOptions = useMemo(() => {
    if (!locationId || !units) return [];
    const amenities = units?.flatMap((unit) => unit?.raw.unit_amenities?.map((amenity) => amenity.name)) || [];
    const uniqueAmenities = Array.from(new Set(amenities));
    return uniqueAmenities
      .map((amenity) => ({
        label: amenity?.toString() ?? "--",
        value: amenity?.toString() ?? "--",
        disabled: false,
        checked: false,
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [units, locationId]);

  const filterOptions = useMemo<FilterOption[]>(() => {
    return [
      {
        id: "sizes",
        label: "Sizes",
        filters: sizesFilterOptions,
      },
      {
        id: "amenities",
        label: "Amenities",
        filters: amenitiesFilterOptions,
      },
    ];
  }, [amenitiesFilterOptions, sizesFilterOptions]);

  const checkedFilterOptions = useMemo(() => {
    if (!locationId || !filterOptions) return [];

    return filterOptions?.map((option) => ({
      ...option,
      filters: option.filters.map((filter) => ({
        ...filter,
        checked: activeFilters[option.id]?.has(filter.value) ?? false,
      })),
    }));
  }, [activeFilters, locationId, filterOptions]);

  useEffect(() => {
    LocationsModel.getGroupsAndLocations(false);
    TenantsModel.getTenantsByUserId(user.id);
    if (locationId) {
      UnitsModel.getUnitsByLocation(locationId);
    }
  }, [locationId, user.id]);

  // reset search params on component mount to ensure no stale params
  useEffect(() => {
    setMultipleSearchParams({
      locationId: undefined,
      order: undefined,
      tab: undefined,
      tableValue: undefined,
      unitId: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterLocations = (search: string) => {
    if (!search) {
      setFilteredLocationsByGroup(groups || []);
      return;
    }

    const loweredSearch = search.toLowerCase();

    const newlyFilteredLocationsByGroup =
      groups?.map((group) => ({
        ...group,
        locations: group.locations.filter((location) => {
          const nameMatches = location.name.toLowerCase().includes(loweredSearch);
          // @ts-expect-error need to fix typing here
          const address = locationsWithVariables?.[location?.id]?.variables?.Address?.value.toLowerCase() || "";
          const addressMatches = address && address.includes(loweredSearch);
          return nameMatches || addressMatches;
        }),
      })) ?? [];

    setFilteredLocationsByGroup(newlyFilteredLocationsByGroup);
  };

  function applyFilter(groups: GroupInterface[], activeFilters: Record<string, Set<string>>) {
    return groups?.map((group) => ({
      ...group,
      locations: Array.isArray(group.locations)
        ? group.locations.filter((location) => {
          const locationUnits = unitsByLocation?.[location.id] || [];

          const locationAmenities = locationUnits?.map((unit) =>
            Array.isArray(unit.raw.unit_amenities) ? unit.raw.unit_amenities.flatMap((amenity) => amenity.name) : [],
          );
          const locationAmenitiesSet = new Set(locationAmenities?.flat());
          const locationSizes = new Set(locationUnits?.map((unit) => unit.dimensions));

          const amenitiesFilter = activeFilters["amenities"];
          const sizesFilter = activeFilters["sizes"];

          // skip filtering if no filters applied
          const hasMatchingAmenities = !amenitiesFilter || Array.from(amenitiesFilter).every((filter) => locationAmenitiesSet.has(filter));
          const hasMatchingSizes = !sizesFilter || Array.from(sizesFilter).every((filter) => locationSizes.has(filter));
          return hasMatchingAmenities && hasMatchingSizes;
        }).sort((a, b) => {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();
          return aName.localeCompare(bName);
        })
        : [],
    }));
  }

  useEffect(() => {
    if (!locationId || !groups) return;

    setFilteredLocationsByGroup(applyFilter(groups, activeFilters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters, groups, locationId]);

  const handleSaveFilters = ({ optionId, value, checked }: { optionId: string; value: string; checked: boolean }) => {
    setActiveFilters((prevFilter) => {
      const newSet = new Set(prevFilter[optionId] ?? []);
      checked ? newSet.add(value) : newSet.delete(value);
      return { ...prevFilter, [optionId]: newSet };
    });
  };

  const handleResetFilters = () => {
    setActiveFilters({});
  };

  const handleOpenPanel = (locationId: number) => {
    setMultipleSearchParams({ locationId: String(locationId) });
    setOpenPanel(true);
  };

  const handleClosePanel = () => {
    setMultipleSearchParams({ locationId: undefined, unitId: undefined });
    setOpenPanel(false);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2 bg-white rounded-md p-4">
        <Input
          debounce={100}
          onChange={(e) => handleFilterLocations(e.target.value)}
          placeholder="Search store names or location"
          isSearch
          style={{ borderRadius: "9999px" }}
        />

        <div className="flex gap-3 items-center">
          <span>Filters:</span>
          <div className="flex flex-wrap gap-2 gap-y-1">
            <FilterDropdown id="location-filters" filterOptions={checkedFilterOptions} resetFilters={handleResetFilters} saveFilters={handleSaveFilters} />
            {activeFilters &&
              Object.entries(activeFilters).map(([optionId, values]) => {
                const option = filterOptions.find((opt) => opt.id === optionId);
                if (!option) return null;

                const selectedFilters = option.filters.filter((filter) => values.has(filter.value));
                return selectedFilters.map((filter: { label: string; value: string }) => (
                  <button
                    aria-label={`remove filter ${filter.label}`}
                    className="bg-orange-50 max-h-8 max-w-fit rounded-md font-semibold py-1 px-3"
                    key={filter.value}
                    onClick={() => handleSaveFilters({ optionId, value: filter.value, checked: false })}
                  >
                    {filter.label}
                    <span className="text-orange-500 ml-2.5">x</span>
                  </button>
                ));
              })}
          </div>
        </div>
      </div>

      {/* group name card & location card list */}
      {Array.isArray(filteredLocationsByGroup) &&
        filteredLocationsByGroup.length > 0 &&
        filteredLocationsByGroup.map((group) => {
          return (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {group.locations.length > 0 ? (
                <div key={group.id} className="flex mb-3 flex-col divide-y divide-grey-400">
                  <div className="bg-white py-4 sticky top-0 rounded-t-md font-semibold text-grey-600 px-4">
                    {group.name}
                  </div>
                  {group.locations.map((location: LocationInterface) => (
                    <button className="w-full cursor-pointer last:rounded-b-md overflow-hidden" onClick={() => handleOpenPanel(location.id)} key={location.id}>
                      <DataCardLocation
                        classNames={{ wrapper: "hover:bg-orange-200" }}
                        title={location.name}
                        isRounded={false}
                        locationId={location.id}
                        variant="secondary"
                        pills={
                          userVars &&
                          Number(userVars?.[UserVarKey.Location]) === location.id && [
                            <Pill type={PillType.ORANGE} size={PillSize.SMALL} style={PillStyle.LIGHT} text="Selected" />,
                          ]
                        }
                      />
                    </button>
                  ))}
                </div>
              ) : null}
            </>
          );
        })}

      <DetailsPanel
        isOpen={openPanel}
        onClose={handleClosePanel}
        renderTab={(tab, navigateForward) => {
          switch (tab) {
            case "Store Details":
              return <UserDetailsLocationDetailsView navigateForward={navigateForward} user={user} userVars={userVars} />;
            // case "Location Variables List View":
            //   return null;
            case "Unit Details":
              return <UserDetailsUnitDetailsView navigateForward={navigateForward} user={user} userVars={userVars} />;
            default:
              return null;
          }
        }}
        tabs={["Store Details", "Unit Details"]}
      />
    </div>
  );
}
